import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/api";
import { useAuth } from "@/auth/AuthProvider";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { relativeTime } from "@/lib/format";
import { STATE_STYLES } from "@/lib/format";

const POLL_MS = 15000;

export default function Alerts() {
  const { user } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [filter, setFilter] = useState("all");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/alerts?status=${filter}&limit=100`);
      setAlerts(data);
      setError("");
    } catch (err) {
      setError(formatApiError(err));
    }
  }, [filter]);

  useEffect(() => {
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  const ack = async (id) => {
    try {
      await api.post(`/alerts/${id}/ack`);
      toast.success("Alert acknowledged");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <AppShell>
      <div className="mb-6 flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">
            Audit log
          </div>
          <h1 className="font-display text-4xl font-black uppercase tracking-tight">
            Alerts
          </h1>
        </div>
        <div className="flex gap-1 border border-zinc-800" data-testid="alerts-filter">
          {["all", "active"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`alerts-filter-${f}`}
              className={`px-3 py-2 text-xs uppercase tracking-widest ${
                filter === f
                  ? "bg-zinc-100 text-zinc-950"
                  : "text-zinc-400 hover:text-zinc-100"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div data-testid="alerts-error" className="mb-6 text-sm text-red-400 border-l-2 border-red-500 pl-3">
          {error}
        </div>
      )}

      <div className="border border-zinc-800" data-testid="alerts-table-wrap">
        <table className="w-full text-sm" data-testid="alerts-table">
          <thead className="bg-zinc-900/50 text-[10px] uppercase tracking-widest text-zinc-500">
            <tr>
              <th className="text-left p-3">Sensor</th>
              <th className="text-left p-3">Severity</th>
              <th className="text-left p-3">Opened</th>
              <th className="text-left p-3">Closed</th>
              <th className="text-left p-3">Trigger</th>
              <th className="text-left p-3">Ack</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800">
            {alerts.length === 0 && (
              <tr>
                <td colSpan={7} className="p-6 text-center text-zinc-500 text-sm">
                  No alerts.
                </td>
              </tr>
            )}
            {alerts.map((a) => {
              const style = STATE_STYLES[a.severity];
              const isActive = !a.closed_at;
              return (
                <tr key={a.id} data-testid={`alerts-row-${a.id}`} className="hover:bg-zinc-900/40">
                  <td className="p-3 text-zinc-200">{a.sensor_name}</td>
                  <td className="p-3">
                    <span
                      className={`text-[10px] uppercase tracking-widest border px-2 py-1 ${style.pill}`}
                    >
                      {style.label}
                    </span>
                  </td>
                  <td className="p-3 text-zinc-400" title={a.opened_at}>
                    {relativeTime(a.opened_at)}
                  </td>
                  <td className="p-3 text-zinc-400" title={a.closed_at || ""}>
                    {a.closed_at ? relativeTime(a.closed_at) : <span className="text-red-400">active</span>}
                  </td>
                  <td className="p-3 text-zinc-300 font-mono text-xs">
                    {a.trigger_value_f.toFixed(1)}°F{" "}
                    <span className="text-zinc-500">
                      ({a.trigger_field.replace("_", " ")} {a.threshold_value_f}°F)
                    </span>
                  </td>
                  <td className="p-3 text-zinc-400">
                    {a.ack_at ? relativeTime(a.ack_at) : "—"}
                  </td>
                  <td className="p-3 text-right">
                    {isActive && !a.ack_at && (
                      <Button
                        size="sm"
                        onClick={() => ack(a.id)}
                        data-testid={`alerts-ack-${a.id}`}
                        className="rounded-sm bg-zinc-100 text-zinc-950 hover:bg-white uppercase tracking-widest text-xs font-bold"
                      >
                        Ack
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </AppShell>
  );
}
